import { useContext, useState }                                     from "react"
import Page                                                         from '../../components/Page'
import CommonStatisticsPage                                         from '../../common/CommonStatisticsPage'
import {
    Checkbox, Container, FormControlLabel, Stack,
    Typography, TableBody, TableCell,
    TableFooter, TableHead, TableRow,
}                                                                   from '@mui/material'
import { useTranslation }                                           from 'react-i18next'
import styled                                                       from '@emotion/styled'
import { useApi }                                                   from '../../utils/api'
import { fExportPrice, fOperatorClass, fPrice }                     from '../../utils/textutils'
import { AppContext }                                               from '../../App'
import { User }                                                     from '../../consts'

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&.OperatorRow .MuiTableCell-root': {
    color: '#ffffff',
  },
  '&.OperatorRow.Orange': {
    backgroundColor: '#f16e00',
  },
  '&.OperatorRow.Telekom': {
    backgroundColor: '#e20074',
  },
  '&.OperatorRow.O2': {
    backgroundColor: '#0090d0',
  },
  '&.OperatorRow.Swan': {
    backgroundColor: '#fad210',
    '& .MuiTableCell-root': {
      color: '#000000',
    }
  },
}))

const highlightOperatorKey = 'highlightOperator'

const MessageStatisticsPage = () => {
  const api = useApi()
  const { t } = useTranslation()
  const { user } = useContext(AppContext)
  const [highlightOperator, setHighlightOperator] = useState(localStorage.getItem(highlightOperatorKey) === 'true')
  const setHighlightOperatorImpl = (e) => {
    localStorage.setItem(highlightOperatorKey, e.target.checked)
    setHighlightOperator(e.target.checked)
  }

  return (
    <Page title={t('Message statistics')}>
      <Container component="main">
        <Typography variant="h4" gutterBottom>{t('Message statistics')}</Typography>
        <CommonStatisticsPage
          fnApiLoadData={user.type === User.typeViewer ? api.viewer.message.statistics : api.message.statistics}
          pathPrefix={'/frontend/dashboard/statistics/messages'}
          defaultGroupBy={['service', 'operator']}
          allowedGroupBy={['service', 'partner', 'category', 'provider', 'operator', 'status', 'price']}
          exportFileName={'messages'}
          exportRow={(row, filter) => {
            let obj = {}
            if (filter.groupBy.includes('service')) {
              obj[t('Service')] = row.service?.name ?? ''
            }
            if (filter.groupBy.includes('partner')) {
              obj[t('Partner')] = row.partner?.name ?? ''
            }
            if (filter.groupBy.includes('category')) {
              obj[t('Category')] = row.category?.title ?? ''
            }
            if (filter.groupBy.includes('provider')) {
              obj[t('Provider')] = row.provider?.name ?? ''
            }
            if (filter.groupBy.includes('operator')) {
              obj[t('Operator')] = row.operator ?? ''
            }
            if (filter.groupBy.includes('status')) {
              obj[t('Processing status')] = row.processingStatus ? t(row.processingStatus) : ''
              obj[t('Delivery status')] = row.deliveryStatus ? t(row.deliveryStatus) : ''
            }
            if (filter.groupBy.includes('price')) {
              obj[t('FinalUnitPrice')] = fExportPrice(row.priceWithVat ?? 0)
            }
            obj[t('Count')] = row.count ?? 0
            obj[t('PriceNoVat')] = fExportPrice(row.sumPrice ?? 0)
            obj[t('Vat')] = fExportPrice(row.sumVat ?? 0)
            obj[t('PriceWithVat')] = fExportPrice(row.sumPriceWithVat ?? 0)
            return obj
          }}
          customOptions={
            <Stack key={'stack-option'} alignItems={'end'}>
              <FormControlLabel control={<Checkbox checked={highlightOperator} onChange={setHighlightOperatorImpl} />} label={t('Highlight operator')} />
            </Stack>
          }
        >
          <StatsTableHeader />
          <StatsTableBody highlightOperator={highlightOperator} />
          <StatsTableFooter />
        </CommonStatisticsPage>
      </Container>
    </Page>)
}

const StatsTableHeader = ({ t, filter }) => {
  return <TableHead>
    <TableRow>
      {filter.groupBy.includes('service') && <TableCell>{t('Service')}</TableCell>}
      {filter.groupBy.includes('partner') && <TableCell>{t('Partner')}</TableCell>}
      {filter.groupBy.includes('category') && <TableCell>{t('Category')}</TableCell>}
      {filter.groupBy.includes('provider') && <TableCell>{t('Provider')}</TableCell>}
      {filter.groupBy.includes('operator') && <TableCell>{t('Operator')}</TableCell>}
      {filter.groupBy.includes('status') && <TableCell>{t('Processing status')}</TableCell>}
      {filter.groupBy.includes('status') && <TableCell>{t('Delivery status')}</TableCell>}
      {filter.groupBy.includes('price') && <TableCell>{t('FinalUnitPrice')}</TableCell>}
      <TableCell align={'right'}>{t('Count')}</TableCell>
      <TableCell align={'right'}>{t('PriceNoVat')}&nbsp;€</TableCell>
      <TableCell align={'right'}>{t('Vat')}&nbsp;€</TableCell>
      <TableCell align={'right'}>{t('PriceWithVat')}&nbsp;€</TableCell>
    </TableRow>
  </TableHead>
}

const StatsTableBody = ({ t, filter, data, highlightOperator }) => {
  return <TableBody>
    {data.map((row, i) => <StyledTableRow className={highlightOperator ? `OperatorRow ${fOperatorClass(row.operator)}` : ''} key={`row-${i}`}>
      {filter.groupBy.includes('service') && <TableCell>{row.service?.name ?? ''}</TableCell>}
      {filter.groupBy.includes('partner') && <TableCell>{row.partner?.name ?? ''}</TableCell>}
      {filter.groupBy.includes('category') && <TableCell>{row.category?.title ?? ''}</TableCell>}
      {filter.groupBy.includes('provider') && <TableCell>{row.provider?.name ?? ''}</TableCell>}
      {filter.groupBy.includes('operator') && <TableCell>{row.operator ?? ''}</TableCell>}
      {filter.groupBy.includes('status') && <TableCell>{row.processingStatus ? t(row.processingStatus) : ''}</TableCell>}
      {filter.groupBy.includes('status') && <TableCell>{row.deliveryStatus ? t(row.deliveryStatus) : ''}</TableCell>}
      {filter.groupBy.includes('price') && <TableCell>{fPrice(row.priceWithVat ?? 0)}</TableCell>}
      <TableCell align={'right'}>{row.count ?? 0}</TableCell>
      <TableCell align={'right'}>{fPrice(row.sumPrice ?? 0)}</TableCell>
      <TableCell align={'right'}>{fPrice(row.sumVat ?? 0)}</TableCell>
      <TableCell align={'right'}>{fPrice(row.sumPriceWithVat ?? 0)}</TableCell>
    </StyledTableRow>)}
  </TableBody>
}

const StatsTableFooter = ({ t, filter, data }) => {
  const sum = (prop) => data.map((row) => row[prop] ?? 0).reduce((acc, curr) => acc + curr, 0)
  // 'status' is rendered as 2 columns (processing & delivery status)
  const countOfEmptyCells = ['service', 'partner', 'category', 'provider', 'operator', 'status', 'status', 'price'].map(groupBy => filter.groupBy.includes(groupBy)).filter(it => it).length - 1

  return <TableFooter>
    <TableRow>
      <TableCell component={'th'} scope="row">{t('Total')}</TableCell>
      {Array(countOfEmptyCells).fill().map((_, i) => <TableCell key={`cell-${i}`}>&nbsp;</TableCell>)}
      <TableCell align={'right'}>{sum('count')}</TableCell>
      <TableCell align={'right'}>{fPrice(sum('sumPrice'))}</TableCell>
      <TableCell align={'right'}>{fPrice(sum('sumVat'))}</TableCell>
      <TableCell align={'right'}>{fPrice(sum('sumPriceWithVat'))}</TableCell>
    </TableRow>
  </TableFooter>
}

export default MessageStatisticsPage